import {
  DeviceOnGridType,
  IGrid,
  IGridDetails,
  IMousePosition,
  IMoveReferentDevice,
  IPlacedDevice,
  ISegment
} from '../models/grid.model';
import {
  DECREASE_COMBUS_BUSBAR_RAILS_TOP_SIZE_BY, DECREASE_POWER_TAG_RAILS_TOP_SIZE_BY,
  DECREASE_RAILS_TOP_SIZE_BY,
  ENCLOSURE_DEFAULT_DIMENSIONS,
  HEIGHT_OF_COVERS_LABEL_ROW_IN_PIXELS,
  HEIGHT_OF_ONE_COMB_RAIL_IN_PIXELS, HEIGHT_OF_ONE_POWER_TAG_RAIL_IN_PIXELS,
  HEIGHT_OF_ONE_RAIL_IN_PIXELS,
  MAX_FIELD_ENCLOSURE_SIZE,
  MIN_FIELD_ENCLOSURE_SIZE,
  SLOT_PER_MODULE,
} from '../models/enclosure.constants';
import { IProduct } from '../../shared/models/product.model';
import {
  generateAvailableWidthForNotFittingPattern,
  getAvailableSizeOfCombBar,
  getCombusBusbarDevices,
  gridPositionIndexesAreEqual,
  isPlacedOnHalfOfAnotherDevice,
  overlappingDeviceOnRightSide,
  patternFits,
  railsWidthTooShortForPlacingDevice
} from './grid.helper';
import { IElementOnGrid, IElementSize } from '../../prisma-xs/models/grid-elements.model';
import { PrismaXsConstant } from '../../shared/models/prisma-xs.constant';
import { IUprightOnGrid, UprightOrientation } from '../../prisma-xs/models/upright.model';
import { IRectangle, Rectangle } from '../../widget-grid/models/Rectangle.model';
import { ICdkDragPreview, IPosition, IWidget, PositionSide } from '../../prisma-xs/models/prisma-xs.model';
import { generateUprightReference } from '../../prisma-xs/helpers/prisma-xs.helper';
import { EnclosureTypes } from '../models/enclosure-type.model';
import { IEnclosure } from '../../shared/models/enclosure.model';
import { getRandomValueBetween0And1With16DecimalPlaces } from '../../shared/helpers/math-operations.helper';

export const heightOfOneRailInPixels = (deviceType: DeviceOnGridType): number => {
  switch (deviceType) {
    case DeviceOnGridType.device:
      return HEIGHT_OF_ONE_RAIL_IN_PIXELS;
    case DeviceOnGridType.combBar:
      return HEIGHT_OF_ONE_COMB_RAIL_IN_PIXELS;
    case DeviceOnGridType.powerTag:
      return HEIGHT_OF_ONE_POWER_TAG_RAIL_IN_PIXELS;
    default:
      return 100;
  }
};

export const topPositionOfRailsDecrease = (deviceType: DeviceOnGridType): number => {
  switch (deviceType) {
    case DeviceOnGridType.device:
      return DECREASE_RAILS_TOP_SIZE_BY;
    case DeviceOnGridType.combBar:
      return DECREASE_COMBUS_BUSBAR_RAILS_TOP_SIZE_BY;
    case DeviceOnGridType.powerTag:
      return DECREASE_POWER_TAG_RAILS_TOP_SIZE_BY;
    default:
      return 0;
  }
};

export const getPercentOf = (percentage: number, value: number): number => {
  return value * (percentage / 100);
};

export const calculatePercentOfNumber = (part: number, value: number): number => {
  return part * (100 / value);
};

export const gridRatio = (height: number, width: number) => {
  return height / width;
};

export const calculateLeftOffset = (leftOffset: number, enclosurePictureWidth: number, containerWidth): number => {
  const leftPositionOfRailInPercents = calculatePercentOfNumber(leftOffset, enclosurePictureWidth);

  return getPercentOf(leftPositionOfRailInPercents, containerWidth);
};

export const getRailsDimensionsWidth = (rowWidth: number, enclosurePictureWidth: number, containerWidth: number): number => {
  return getPercentOf(calculatePercentOfNumber(rowWidth, enclosurePictureWidth), containerWidth);
}

export const calculateWidthOfOneField = (rowWidth: number,
                                         enclosurePictureWidth: number,
                                         containerWidth: number,
                                         fieldsLength: number,
                                         product: IProduct): number => {
  if (product.specialDevicePlacement) {
    return getRailsDimensionsWidth(product.specialDevicePlacement?.deviceSpecWidth, enclosurePictureWidth, containerWidth)
      / product.specialDevicePlacement?.numberOfSlots;
  }
  return getRailsDimensionsWidth(rowWidth, enclosurePictureWidth, containerWidth) / fieldsLength;
};

export const calculateTopPositionOfRailInPercents = (topOffset: number, gridType: DeviceOnGridType, enclosurePictureHeight: number) => {
  const decreaseTopPositionBy = topPositionOfRailsDecrease(gridType);

  return calculatePercentOfNumber(
    topOffset - decreaseTopPositionBy, enclosurePictureHeight);
};

export const calculateTopPositionOfDeviceInPercentsFromProductSupportSpec = (topOffset: number,
                                                                             device: IProduct,
                                                                             enclosurePictureHeight: number) => {
  return calculatePercentOfNumber(
    topOffset - decreaseTopPositionByProductType(device), enclosurePictureHeight);
};

const decreaseTopPositionByProductType = (device: IProduct): number => {
  if (device?.productCharacteristicsRules?.isCombBusBar) {
    return DECREASE_COMBUS_BUSBAR_RAILS_TOP_SIZE_BY - (device?.gridPlacementRules?.offsetInCombBusBarPosition || 0);
  }

  if (device?.productCharacteristicsRules?.isPowerTag) {
    return DECREASE_POWER_TAG_RAILS_TOP_SIZE_BY + device.productSupportSpec.positionY;
  }

  return device.productSupportSpec.positionY;
}

export const calculateTopPositionOfWidgetWhileDragging = (topRowPosition: number,
                                                          gridDevicesType: DeviceOnGridType,
                                                          enclosurePictureHeight: number,
                                                          currentRowPosition: number,
                                                          selectedWidgetProduct: IProduct,
                                                          containerHeight: number,
                                                          parentElementHeight: number) => {
  const topPositionOfSegmentInPercents = calculateTopPositionOfRailInPercents(
    topRowPosition, gridDevicesType, enclosurePictureHeight);
  const positionOfCurrent = calculatePercentOfNumber(
    currentRowPosition - getHowMuchRailsTopIsDecreasedByProductType(selectedWidgetProduct), containerHeight);

  return getPercentOf(positionOfCurrent, parentElementHeight) -
    getPercentOf(topPositionOfSegmentInPercents, parentElementHeight);
}

const getHowMuchRailsTopIsDecreasedByProductType = (device: IProduct): number => {
  if (device?.productCharacteristicsRules?.isCombBusBar) {
    return DECREASE_COMBUS_BUSBAR_RAILS_TOP_SIZE_BY- (device?.gridPlacementRules?.offsetInCombBusBarPosition || 0);
  }

  if (device?.productCharacteristicsRules?.isPowerTag) {
    return DECREASE_POWER_TAG_RAILS_TOP_SIZE_BY;
  }

  return DECREASE_RAILS_TOP_SIZE_BY;
}

export const calculateHeightOfOneRail = (
  containerHeight: number, enclosurePictureHeight: number, _: number, gridDevicesType: DeviceOnGridType): number => {
  const railsHeightToImage = heightOfOneRailInPixels(gridDevicesType);

  const percent = calculatePercentOfNumber(railsHeightToImage, enclosurePictureHeight);
  return getPercentOf(percent, containerHeight);
};

export const calculateHeightOfCoversLabelRow = (
  containerHeight: number, enclosurePictureHeight: number): number => {
  const percent = calculatePercentOfNumber(HEIGHT_OF_COVERS_LABEL_ROW_IN_PIXELS, enclosurePictureHeight);
  return getPercentOf(percent, containerHeight);
};

export const calculateHeightOfMainContainer = (
  clientHeight: number, clientWidth: number, _: number, heightWidthRatio: number, topOffset = 0): number => {
  let containerHeight = clientHeight - topOffset;
  let containerWidth = containerHeight / heightWidthRatio;
  if (containerWidth > clientWidth) {
    containerWidth = clientWidth;
    containerHeight = containerWidth * heightWidthRatio;
  }
  return containerHeight;
};

export const calculateImageWidthAndHeightBasedOnZoom = (height: number,
                                                        width: number,
                                                        parentElement: HTMLElement,
                                                        zoomFactor: number,
                                                        topBlockingContainer: HTMLElement) => {

  const heightWidthRatio = height / width;
  const parentRect = parentElement?.getBoundingClientRect();
  const containerHeight = calculateHeightOfMainContainer(
    parentRect.height, parentRect.width, zoomFactor, heightWidthRatio,
    topBlockingContainer?.getBoundingClientRect()?.height);
  const zoomBy = 50 * (zoomFactor - 1);

  return {
    height: containerHeight + zoomBy,
    width: (containerHeight + zoomBy) / heightWidthRatio
  };
};

// @TODO Cover with Unit Test
export const getResizeZoomFactor = (oldZoomFactor: number, newZoomFactor: number) => {
  if (newZoomFactor !== oldZoomFactor) {
    // If zoom factor is not 1 and image is zoomed, the enclosure will fit to window on window resize action
    return newZoomFactor;
  }

  // If image is fitting to window and zoom factor is 1, then zoom factor different then 1 need to be passed,
  // in order to trigger the grids recreation. Small number between 0 and 1 divided by 100 is added.
  const random = getRandomValueBetween0And1With16DecimalPlaces() / 100;
  return newZoomFactor + random;
}

export const calculateZoomFactorBasedOnImageDimensions = (
  parentElement: HTMLElement,
  gridContainerContentHeight: number): number => {
  const containerHeight = parentElement?.getBoundingClientRect()?.height;
  const zoomBy = gridContainerContentHeight - containerHeight;

  return (zoomBy / 50) + 1;
}

// @TODO Cover with Unit Test
export const calculateZoomFactorBasedOnImageDimensionsForPrismaXSEnclosures = (selectedEnclosure: IEnclosure,
                                                                               switchboardContainerHeight: number,
                                                                               zoomControlsContainerHeight: number,
                                                                               enclosureZoomLevel: number,
                                                                               parentElement: HTMLElement): number => {
  const sizeOfOneField = calculatePrismaXSFieldSize(
    switchboardContainerHeight - zoomControlsContainerHeight + (50 * enclosureZoomLevel), selectedEnclosure.rows);

  const heightOfContent = sizeOfOneField.height * selectedEnclosure.rows;

  const containerHeight = parentElement?.getBoundingClientRect()?.height;
  const zoomBy = heightOfContent - containerHeight;
  const zoomFactor = (zoomBy / 50) + 1;

  return Math.round(zoomFactor);
}

export const diagonalDistance = (width: number, height: number): number => {
  return Math.sqrt(Math.pow(Math.abs(width) + Math.abs(height), 2));
};

export const calculatePrismaXSFieldSize = (parentHeight, rows: number): IElementSize => {
  const height = parentHeight/rows;
  const width = height * PrismaXsConstant.GRID_BOX_PROPORTION;

  return { width, height };
};

export const uprightsOnDOMDimensions = (upright: IUprightOnGrid,
                                        parentRectangleDimensions: IElementOnGrid): IRectangle => {
  const uprightsDimensions = { top: 0, left: 0, width: 0, height: 0 }

  if (upright.orientation === UprightOrientation.VERTICAL) {
    uprightsDimensions.left = (upright.placePosition.left - 1) * parentRectangleDimensions.widthOfOne
    - (upright.positionSide === PositionSide.RIGHT ? 0 : PrismaXsConstant.UPRIGHT_WIDTH);
    uprightsDimensions.top = (upright.placePosition.top - 1) * parentRectangleDimensions.heightOfOne;
    uprightsDimensions.width = PrismaXsConstant.UPRIGHT_WIDTH;
    uprightsDimensions.height = parentRectangleDimensions.heightOfOne * upright.dimensions.height;
  }
  else {
    uprightsDimensions.left = (upright.placePosition.left - 1) * parentRectangleDimensions.widthOfOne;
    uprightsDimensions.top = (upright.placePosition.top - 1) * parentRectangleDimensions.heightOfOne
      - (upright.positionSide === PositionSide.UP ? PrismaXsConstant.UPRIGHT_WIDTH : 0);
    uprightsDimensions.width = parentRectangleDimensions.widthOfOne * upright.dimensions.width;
    uprightsDimensions.height = PrismaXsConstant.UPRIGHT_WIDTH;
  }

  return uprightsDimensions;
};

export const getUprightsImage = (upright: IUprightOnGrid): string => {
  if (upright.orientation === UprightOrientation.VERTICAL) {
    return generateUprightReference(upright?.dimensions?.height);
  } else {
    return generateUprightReference(upright?.dimensions?.width, false);
  }
}

export const getUprightBackgroundPosition = (upright: IUprightOnGrid): 'top' | 'bottom' | 'right' | '' => {
  if (upright.orientation === UprightOrientation.VERTICAL) {
    return upright.positionSide === PositionSide.LEFT ? 'right' : ''
  } else {
    return upright.positionSide === PositionSide.DOWN ? 'top' : 'bottom'
  }
};

export const getZoomByDimension = (zoomIndex: number): number => {
  return 50 * (zoomIndex - 1);
};

export const getKitWithMaxRowsNumber = (productsOnEnclosure: IProduct[]): IProduct => {
  const products: IProduct[] = [].concat(productsOnEnclosure);
  products.sort((a, b) => {
    if (a.productSupportSpec.productSupportSpecPoints.length < b.productSupportSpec.productSupportSpecPoints.length) {
      return 1;
    } else {
      return -1;
    }
  });
  const [firstProductWithRows] = products;
  return firstProductWithRows;
};

const productsContainsProductWithAllRangesAllowed = (products: IProduct[]): boolean => {
  return products.some(product => {
    if (product?.gridPlacementRules?.kitsPlacementWithSpecialRules) {
      return;
    }
    return product.productSupportSpec.productSupportSpecPoints.some(point => !point.supportedRangesList?.length)
  })
}

const getOnlySupportedRangesOnGrid = (products: IProduct[]): number[] => {
  let onlySupportedRangesOnGrid = [];

  if (productsContainsProductWithAllRangesAllowed(products)) {
    return [];
  }
  products.forEach(product => {
    product.productSupportSpec.productSupportSpecPoints.forEach(point => {
      const allowedRanges = point.supportedRangesList.filter(supportedRange => !onlySupportedRangesOnGrid.includes(supportedRange));
      onlySupportedRangesOnGrid = onlySupportedRangesOnGrid.concat(allowedRanges);
    })
  })

  return onlySupportedRangesOnGrid;
}

export const getPrismaXSGridDimensionsDetails = (products: IProduct[], enclosureRowsNumber: number): IGridDetails => {
  if (!products?.length) {
    return null;
  }
  const enclosureImageDimensions = {
    containerWidth: ENCLOSURE_DEFAULT_DIMENSIONS.oneFieldDefaultWidth,
    containerHeight: ENCLOSURE_DEFAULT_DIMENSIONS.oneFieldDefaultHeight * enclosureRowsNumber
  };
  return {
    enclosureImageDimensions,
    firstRowDimensionsWidth: ENCLOSURE_DEFAULT_DIMENSIONS.rowDimensionsWidth,
    firstRowSlotsWidth: ENCLOSURE_DEFAULT_DIMENSIONS.rowSlotsWidth,
    heightWidthRatio: Math.round(gridRatio(enclosureImageDimensions.containerHeight, enclosureImageDimensions.containerWidth) * 100) / 100,
    enclosureRows: enclosureRowsNumber,
    onlySupportedRangesOnGrid: getOnlySupportedRangesOnGrid(products),
    specialKitsOnGridReferences: getPrismaXSProductsWitSpecialPlacementReferences(products)
  }
};

export const getEnclosuresGridDimensionsDetails = (products: IProduct[]): IGridDetails => {
  const productsWithoutSpecialPlacement = getProductsWithoutSpecialPlacement(products);

  const firstProductWithRows = getKitWithMaxRowsNumber(
    productsWithoutSpecialPlacement?.length ? productsWithoutSpecialPlacement : products);
  if (!firstProductWithRows?.productSupportSpec?.productSupportSpecPoints?.length) {
    return null;
  }
  const enclosureImageDimensions = {
    containerWidth: firstProductWithRows?.productSupportSpec?.width,
    containerHeight: firstProductWithRows?.productSupportSpec?.height
  };

  return {
    enclosureImageDimensions,
    heightWidthRatio: Math.round(gridRatio(
      enclosureImageDimensions.containerHeight,
      enclosureImageDimensions.containerWidth) * 100) / 100,
    onlySupportedRangesOnGrid: [],
    firstRowDimensionsWidth: firstProductWithRows?.productSupportSpec?.productSupportSpecPoints[0]?.width,
    firstRowSlotsWidth: firstProductWithRows?.enclosureSpecs?.numberOfModulesPerRow * SLOT_PER_MODULE
  }
};

export const resizedWidgetAvailableSize = (size: number,
                                           device: IPlacedDevice,
                                           combGrid: IGrid,
                                           devicesEnclosureGrid: IGrid,
                                           placedDevices: IPlacedDevice[]): number => {
  const widgetNewSize = getAvailableSizeOfCombBar(size,
    device?.device?.gridPlacementRules?.numberOfCombBusBarCuttingSlots);
  const blockingDevice = overlappingDeviceOnRightSide([{
      ...device,
      width: widgetNewSize
    }],
    { position: device.position, width: widgetNewSize },
    getCombusBusbarDevices(placedDevices.filter(d => gridPositionIndexesAreEqual(d.enclosureField, device.enclosureField))));

  const selectedRailWidth = combGrid.rows[device.position.top - 1].fields.length;
  let width = railsWidthTooShortForPlacingDevice(widgetNewSize, device.position, selectedRailWidth) ?
    getAvailableSizeOfCombBar(selectedRailWidth - device.position.left + 1,
      device?.device?.gridPlacementRules?.numberOfCombBusBarCuttingSlots) : widgetNewSize;
  width = width < device.device.sizeRules?.minSize ? device.device.sizeRules?.minSize : width;

  width = blockingDevice ? getAvailableSizeOfCombBar(blockingDevice.position.left - device.position.left,
    device?.device?.gridPlacementRules?.numberOfCombBusBarCuttingSlots) : width;

  const field = combGrid.rows[device.position.top - 1].fields[device.position.left - 1];

  const devicesOnSameRow = placedDevices.filter(placedDevice =>
    gridPositionIndexesAreEqual(placedDevice.enclosureField, device.enclosureField) &&
    !placedDevice?.device.productCharacteristicsRules?.isCombBusBar &&
    field.position.top === placedDevice.position.top);

  const currentGrid = devicesEnclosureGrid;

  if (!patternFits(field.position, width, currentGrid, device.device.pattern)) {
    width = generateAvailableWidthForNotFittingPattern(field, width, currentGrid, { placedDevice: device, newSize: size });
  } else {
    if (isPlacedOnHalfOfAnotherDevice(field.position, width, devicesOnSameRow)) {
      width = device.width;
    }
  }

  return width;
};

// @TODO Cover with Unit Test
export const calculateEnclosureFieldSize = (products: IProduct[],
                                            selectedEnclosure: IEnclosure,
                                            switchboardEl: HTMLElement,
                                            zoomEl: HTMLElement,
                                            zoomLevel: number): number => {
  const enclosureRowsNumber = getEnclosureNumberOfRows(selectedEnclosure);
  const [product] = products;
  const { height } = calculateImageWidthAndHeightBasedOnZoom(
    product?.productSupportSpec?.height, product?.productSupportSpec?.width, switchboardEl,
    zoomLevel, zoomEl);

  return height / enclosureRowsNumber;
};

// @TODO Cover with Unit Test
export const calculateHeightOfOneFieldFromZoomFactor = (products: IProduct[],
                                                        selectedEnclosure: IEnclosure,
                                                        switchboardEl: HTMLElement,
                                                        zoomEl: HTMLElement,
                                                        zoomLevel: number): number => {
  if (selectedEnclosure.enclosureType === EnclosureTypes.PRISMA_XS) {
    return calculatePrismaXSFieldSize(
      switchboardEl.clientHeight - zoomEl.clientHeight + getZoomByDimension(zoomLevel),selectedEnclosure.rows)?.height;
  }
  return calculateEnclosureFieldSize(products, selectedEnclosure, switchboardEl, zoomEl, zoomLevel);
};

// @TODO Cover with Unit Test
export const getEnclosureNumberOfRows = (selectedEnclosure: IEnclosure): number => {
  switch (selectedEnclosure.enclosureType) {
    case EnclosureTypes.PRISMA_XS:
      return selectedEnclosure.rows;
    case EnclosureTypes.PRAGMA:
      return selectedEnclosure.rows;
    case EnclosureTypes.NORMAL:
      // eslint-disable-next-line no-case-declarations
      const [enclosureSpecs] = selectedEnclosure?.enclosureSpecs ?? [];
      return enclosureSpecs.numberOfRows;
  }
};

// @TODO Cover with Unit Test
export const fieldHeightIsShorterThenMin = (products: IProduct[],
                                            selectedEnclosure: IEnclosure,
                                            switchboardEl: HTMLElement,
                                            zoomEl: HTMLElement,
                                            zoomLevel: number): boolean => {
  return calculateHeightOfOneFieldFromZoomFactor(products, selectedEnclosure, switchboardEl, zoomEl, zoomLevel) < MIN_FIELD_ENCLOSURE_SIZE;
};

// @TODO Cover with Unit Test
export const fieldHightIsHigherThenMax = (products: IProduct[],
                                          selectedEnclosure: IEnclosure,
                                          switchboardEl: HTMLElement,
                                          zoomEl: HTMLElement,
                                          zoomLevel: number) => {
  return calculateHeightOfOneFieldFromZoomFactor(products, selectedEnclosure, switchboardEl, zoomEl, zoomLevel) > MAX_FIELD_ENCLOSURE_SIZE;
}

export const generateReferentDevice = (segment: ISegment,
                                       pointerPosition: IMousePosition,
                                       mouseDelta: IMousePosition,
                                       currentDeviceLeft: number,
                                       selectedRowTop: number,
                                       gridDevicesType: DeviceOnGridType,
                                       enclosurePictureHeight: number,
                                       parentContainerHeight,
                                       widthOfOneField): IMoveReferentDevice => {
  const leftPositionOfCurrentWidgetInSegment = currentDeviceLeft - segment.position.left;
  const topPositionOfSegmentInPercents = calculateTopPositionOfRailInPercents(
    segment.dimensions.top, gridDevicesType, enclosurePictureHeight);
  const positionOfCurrent = calculateTopPositionOfRailInPercents(selectedRowTop, gridDevicesType, enclosurePictureHeight);
  const topOfCurrentInSegment = getPercentOf(positionOfCurrent - topPositionOfSegmentInPercents, parentContainerHeight);

  return {
    position: segment.referentDevicePosition,
    devicesRectangle: {
      top: pointerPosition.y - mouseDelta.y - topOfCurrentInSegment,
      left: pointerPosition.x - mouseDelta.x + ((-leftPositionOfCurrentWidgetInSegment) * widthOfOneField),
    }
  };
};

export const getWholeEnclosureHeight = (parentElementClientHeight: number,
                                        topBlockingContainerClientHeight: number,
                                        zoomFactor: number,
                                        selectedEnclosureRows: number): number => {
  const sizeOfOneField = calculatePrismaXSFieldSize(
    parentElementClientHeight - topBlockingContainerClientHeight + getZoomByDimension(zoomFactor), selectedEnclosureRows);

  return sizeOfOneField.height * selectedEnclosureRows;
};

export const heightOfElectricalClamps = (containerHeight: number, enclosurePictureHeight: number): number => {
  const percent = calculatePercentOfNumber(260, enclosurePictureHeight);
  return getPercentOf(percent, containerHeight);

};

export const getProductsWithoutSpecialPlacement = (products: IProduct[]): IProduct[] => {
  return products.filter(product => {
    if (product.productSupportSpec?.productSupportSpecPoints?.some(point => point.adjustedGridToDevices?.length)) {
      return false;
    }
    return !product?.gridPlacementRules?.kitsPlacementWithSpecialRules;
  })
};

export const getPrismaXSProductsWitSpecialPlacementReferences = (products: IProduct[]): string[] => {
  const specialPlacementProductsReferences: string[] = [];
  products.forEach(product => {
    if (product?.gridPlacementRules?.kitsPlacementWithSpecialRules) {
      specialPlacementProductsReferences.push(product.reference);
    }
  });

  return specialPlacementProductsReferences;
}

export const sortDevicesFromSmallestToLargestPosition = (placedDevices: IPlacedDevice[]): IPlacedDevice[] => {
  return placedDevices.sort((a, b) => {
    if (a.enclosureField.left < b.enclosureField.left) {
      return -1;
    }
    if (a.enclosureField.left > b.enclosureField.left) {
      return 1;
    }
    // devices left fields positions are equal
    if (a.enclosureField.top < b.enclosureField.top) {
      return -1;
    }
    if (a.enclosureField.top > b.enclosureField.top) {
      return 1
    }
    // devices both left and top fields positions are equal, devices are on the same field
    if (a.position.top < b.position.top) {
      return -1;
    }
    if (a.position.top > b.position.top) {
      return 1
    }
    if (a.position.left < b.position.left) {
      return -1;
    }
    if (a.position.left > b.position.left) {
      return 1;
    }
  });
}

export const roundToOneDecimalPlace = (numb: number): number => {
  return Math.round(numb * 10) / 10;
}

export const placedWidgetTopValue = (widgetIsRotated: boolean, topPositionOfRailInPercents: number, widgetWidth: number): string => {
  return widgetIsRotated ?
    `calc( ${roundToOneDecimalPlace(topPositionOfRailInPercents)}% + ${ widgetWidth / 2 }px)` :
    `${roundToOneDecimalPlace(topPositionOfRailInPercents)}%`
}

export const placedWidgetLeftValue = (left: number, widthOfOneField, positionX, widgetHeight, widgetIsRotated): string => {
  return `${roundToOneDecimalPlace((left + widthOfOneField * (positionX - 1)) + (widgetIsRotated ? (widgetHeight / 2) : 0))}px`
}

// eslint-disable-next-line max-len
export const catalogueDragAndDropReferencePoint = (pointerPosition: IPosition, previewDimensions: IElementSize, widget: IWidget): IPosition => {
  const oneFieldDimensions: IElementSize = {
    width: previewDimensions.width / widget.width,
    height: previewDimensions.height / widget.height,
  };

  return {
    y: pointerPosition.y + oneFieldDimensions.height / 2,
    x: pointerPosition.x + oneFieldDimensions.width / 2,
  };
}

export const elementHoversDropArea = (dragPreview: ICdkDragPreview, mainElementRect: Rectangle) => {
  const event = dragPreview.element;

  return event.pointerPosition.x < mainElementRect.right &&
    event.pointerPosition.x + (dragPreview.previewDimensions.width / 2) > mainElementRect.left &&
    event.pointerPosition.y < mainElementRect.bottom &&
    event.pointerPosition.y + (dragPreview.previewDimensions.height / 2) > mainElementRect.top;
}
